import React, { memo, useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Skeleton,
  Box,
  Tooltip,
  TableSortLabel,
  useTheme,
  alpha,
  Typography,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ContentPaste as ContentPasteIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { motion } from 'framer-motion';
import type {
  MedicalReservationFilterOptions,
  OutOfHoursDisplayRecord,
} from '../../types/medicalReservation';
import { mapToOutOfHoursDisplayBulk, getDisplayLabel } from '../../utils/outOfHoursDataMapper';
import { getNotificationStatus } from '../../utils/notificationChecker';
import { NOTIFICATION_STATUS_CONFIG } from '../../types/medicalReservation';

interface OutOfHoursReservationTableProps {
  data: OutOfHoursDisplayRecord[];
  isLoading: boolean;
  onViewDetails: (record: OutOfHoursDisplayRecord) => void;
  onEdit: (record: OutOfHoursDisplayRecord) => void;
  onAssign?: (record: OutOfHoursDisplayRecord) => void;
  onDelete?: (record: OutOfHoursDisplayRecord) => void;
  filters: MedicalReservationFilterOptions;
  onFiltersChange: (filters: MedicalReservationFilterOptions) => void;
  meta?: {
    current_page: number;
    total_pages: number;
    total_records: number;
    page_size: number;
  };
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  compactMode?: boolean;
}

/**
 * 指定時間外診療予約レコードテーブルコンポーネント（61項目表示）
 *
 * 意図：
 * - 61項目すべての表示
 * - 横スクロール対応
 * - 主要列の固定表示
 * - 大量データのパフォーマンス最適化
 */
export const OutOfHoursReservationTable = memo<OutOfHoursReservationTableProps>(({
  data,
  isLoading,
  onViewDetails,
  onEdit,
  onAssign,
  onDelete,
  filters,
  onFiltersChange,
  meta,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  compactMode = false,
}) => {
  const theme = useTheme();
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  // 61項目の列定義
  const columns = useMemo(() => {
    const displayKeys = [
      '成果報酬', '自動プログラム使用', '担当カウンセラー', 'カウンセラー対応', '担当医師',
      'anybot ID', '診療開始時間', 'tags', 'ステータス', '会員登録',
      '問診回答', '再診用問診', '登録電話番号', '診療可能な電話番号', '使用ツール',
      '名前（漢字）', '名前（カナ）', '遅延', '診療対応',
      '低用量ピル', '中用量ピル', 'アフターピル', '副作用緩和薬',
      'バイク便', 'CS対応内容', '処方チェック', 'mederi CSから医師へ',
      '処方可能医薬品', '年齢', '親への同意', '服用経験', '服用していたピル', '改善目的', '希望ピル',
      '医師に聞きたいこと', '再診身分証', '再診予約理由', '再診相談内容詳細',
      '前回生理開始日', '次回生理日', '生理を避けたい日にち', '性行為の日時', '妊娠希望',
      '妊娠経験', '妊娠時期・出産回数', '妊娠・出産・授乳有無',
      '過去妊娠中の既往歴', '手術歴', '片頭痛', '既往歴',
      'アレルギー', '身長', '体重', 'BMI',
      '高血圧診断', 'タバコ', '不正出血', '婦人科系疾患の既往歴', 'その他疾患の既往歴',
      'ご家族に血栓症', 'ご家族に乳がん', '身分証', '採血画像'
    ];

    return [
      { id: 'select', label: '', sortable: false, width: 50, sticky: true },
      { id: 'actions', label: 'アクション', sortable: false, width: 120, sticky: true },
      ...displayKeys.map(key => ({
        id: key,
        label: getDisplayLabel(key),
        sortable: false,
        width: key === '診療対応' || key === '処方可能医薬品' || key === '過去妊娠中の既往歴' ? 250 :
               key === '手術歴' || key === '既往歴' ? 200 :
               ['担当医師', 'anybot ID', '名前（漢字）', '診療開始時間'].includes(key) ? 180 : 150,
        sticky: ['担当医師', 'anybot ID', '名前（漢字）', '診療開始時間'].includes(key)
      }))
    ];
  }, []);

  // ソート処理
  const handleSort = (property: string) => {
    const isAsc = filters.sort_by === property && filters.sort_order === 'asc';
    onFiltersChange({
      ...filters,
      sort_by: property as any,
      sort_order: isAsc ? 'desc' : 'asc',
    });
  };

  // ページネーション処理
  const handleChangePage = (event: unknown, newPage: number) => {
    onFiltersChange({
      ...filters,
      page: newPage + 1,
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      page_size: parseInt(event.target.value, 10),
      page: 1,
    });
  };

  // レコードを無効化すべきかどうか判定
  const shouldDisableRecord = (record: OutOfHoursDisplayRecord): boolean => {
    try {
      const consultationDate = new Date(record.consultationStartTime);
      const now = new Date();
      const isPast = consultationDate < now;
      const isListPasted = record.paste_flag === true;
      return isPast && isListPasted;
    } catch {
      return false;
    }
  };

  // 選択可能なデータ数を計算（グレイアウトされていないデータ）
  const selectableData = useMemo(() =>
    data.filter(record => !shouldDisableRecord(record)),
    [data]
  );
  const selectableCount = selectableData.length;

  // 全選択/全解除の判定（選択可能なデータを基準にする）
  const isAllSelected = selectableCount > 0 && selectedIds.length === selectableCount;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < selectableCount;

  // 選択ヘッダーの処理
  const handleSelectAllClick = () => {
    if (isAllSelected) {
      onClearSelection();
    } else {
      onSelectAll();
    }
  };

  // 日時フォーマット
  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), compactMode ? 'MM/dd HH:mm' : 'yyyy/MM/dd HH:mm', { locale: ja });
    } catch {
      return dateString;
    }
  };

  // セルの値を表示用にフォーマット
  const formatCellValue = (value: string, key: string): React.ReactNode => {
    if (!value) return '-';

    // 改行を含むラベルの場合、改行を<br />に変換して表示
    if (key === '診療対応' || key === '処方可能医薬品' || key === '過去妊娠中の既往歴' || key === '手術歴' || key === '既往歴') {
      return (
        <Box sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', lineHeight: 1.4 }}>
          {value}
        </Box>
      );
    }

    // 日付項目の場合
    if (key === '診療開始時間') {
      return formatDateTime(value);
    }

    // 長いテキストの場合は省略してツールチップ表示
    if (value.length > 30) {
      return (
        <Tooltip title={value} placement="top">
          <Typography variant="body2" noWrap sx={{ maxWidth: 140, fontSize: '0.85rem' }}>
            {value}
          </Typography>
        </Tooltip>
      );
    }

    return value;
  };

  if (isLoading) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.slice(0, 10).map((column) => (
                  <TableCell key={column.id} style={{ width: column.width }}>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  {columns.slice(0, 10).map((column) => (
                    <TableCell key={column.id}>
                      <Skeleton variant="text" width="90%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <TableContainer
        sx={{
          maxHeight: compactMode ? 'calc(100vh - 320px)' : 'calc(100vh - 400px)',
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: '9000px', // 61列に対応する最小幅（150px×61列）
          }
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{
                    width: column.width,
                    position: column.sticky ? 'sticky' : 'relative',
                    left: column.sticky ? (column.id === 'select' ? 0 : column.id === 'actions' ? 50 : 'auto') : 'auto',
                    zIndex: column.sticky ? 1200 : 'auto',
                    backgroundColor: column.sticky ? theme.palette.grey[100] : theme.palette.grey[50],
                  }}
                  sx={{
                    fontWeight: 600,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    py: compactMode ? 0.5 : 1,
                    px: 1,
                    whiteSpace: 'normal',
                    fontSize: '0.8rem',
                    lineHeight: 1.2,
                  }}
                >
                  {column.id === 'select' ? (
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAllClick}
                      size="small"
                      disabled={selectableCount === 0}
                    />
                  ) : column.id === 'actions' ? (
                    column.label
                  ) : (
                    <Tooltip title={column.label.replace(/\n/g, ' ')} placement="top">
                      <Box sx={{ textAlign: 'center' }}>
                        {column.label.split('\n').map((line, idx) => (
                          <Box key={idx} component="span" sx={{ display: 'block', fontSize: '0.75rem' }}>
                            {line}
                          </Box>
                        ))}
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                  <Box>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      レコードがありません
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CSVファイルをアップロードしてデータを作成してください
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((record) => {
                const isDisabled = shouldDisableRecord(record);
                return (
                  <TableRow
                    key={record.id}
                    component={motion.tr}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    onMouseEnter={() => !isDisabled && setHoveredRow(record.id)}
                    onMouseLeave={() => !isDisabled && setHoveredRow(null)}
                    sx={{
                      backgroundColor: isDisabled
                        ? theme.palette.grey[100]
                        : hoveredRow === record.id
                        ? alpha(theme.palette.primary.main, 0.04)
                        : 'transparent',
                      opacity: isDisabled ? 0.6 : 1,
                      '&:hover': {
                        backgroundColor: isDisabled
                          ? theme.palette.grey[100]
                          : alpha(theme.palette.primary.main, 0.04),
                      },
                      '& td': {
                        color: isDisabled ? theme.palette.text.disabled : 'inherit',
                      },
                    }}
                  >
                    {/* 選択チェックボックス */}
                    <TableCell
                      sx={{
                        px: 1,
                        py: compactMode ? 0.5 : 1,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1100,
                        backgroundColor: isDisabled ? theme.palette.grey[100] : theme.palette.background.paper,
                      }}
                    >
                      <Checkbox
                        checked={selectedIds.includes(record.id)}
                        onChange={() => onToggleSelect(record.id)}
                        size="small"
                        disabled={isDisabled}
                      />
                    </TableCell>

                    {/* アクションボタン */}
                    <TableCell
                      sx={{
                        px: 1,
                        py: compactMode ? 0.5 : 1,
                        position: 'sticky',
                        left: 50,
                        zIndex: 1100,
                        backgroundColor: isDisabled ? theme.palette.grey[100] : theme.palette.background.paper,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="詳細" placement="top">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onViewDetails(record)}
                              disabled={isDisabled}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="編集" placement="top">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onEdit(record)}
                              disabled={isDisabled || !record.identity_verification_flag}
                            >
                              <ContentPasteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {/* データ項目（61列） */}
                    {columns.slice(2).map((column) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          px: 2,
                          py: compactMode ? 0.5 : 1,
                          fontSize: '0.85rem',
                          position: column.sticky ? 'sticky' : 'relative',
                          left: column.sticky ? 'auto' : 'auto',
                          zIndex: column.sticky ? 1000 : 'auto',
                          backgroundColor: column.sticky
                            ? (isDisabled ? theme.palette.grey[100] : theme.palette.background.paper)
                            : 'transparent',
                          borderRight: column.sticky ? `1px solid ${theme.palette.divider}` : 'none',
                        }}
                      >
                        {formatCellValue(record.displayData[column.id as keyof typeof record.displayData], column.id)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
});

OutOfHoursReservationTable.displayName = 'OutOfHoursReservationTable';